"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { LucideIcon } from "lucide-react";

interface Tab {
  title: string;
  icon: LucideIcon;
  type?: never;
  onClick?: () => void;
  color?: string;
}

interface Separator {
  type: "separator";
  title?: never;
  icon?: never;
  onClick?: never;
  color?: never;
}

type TabItem = Tab | Separator;

interface ExpandableTabsProps {
  tabs: TabItem[];
  className?: string;
  activeColor?: string;
  onChange?: (index: number | null) => void;
  orientation?: "horizontal" | "vertical";
}

const buttonVariantsHorizontal = {
  initial: {
    gap: 0,
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
  },
  animate: (isSelected: boolean) => ({
    gap: isSelected ? ".5rem" : 0,
    paddingLeft: isSelected ? "1rem" : ".5rem",
    paddingRight: isSelected ? "1rem" : ".5rem",
  }),
};

const buttonVariantsVertical = {
  initial: {
    gap: 0,
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
  },
  animate: (isSelected: boolean) => ({
    gap: isSelected ? ".5rem" : 0,
    paddingTop: isSelected ? ".75rem" : ".5rem",
    paddingBottom: isSelected ? ".75rem" : ".5rem",
  }),
};

const spanVariantsHorizontal = {
  initial: { width: 0, opacity: 0 },
  animate: { width: "auto", opacity: 1 },
  exit: { width: 0, opacity: 0 },
};

const spanVariantsVertical = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

const transition = { delay: 0.1, type: "spring" as const, bounce: 0, duration: 0.6 };

export function ExpandableTabs({
  tabs,
  className,
  activeColor = "text-primary",
  onChange,
  orientation = "horizontal",
}: ExpandableTabsProps) {
  const [hovered, setHovered] = React.useState<number | null>(null);
  const outsideClickRef = React.useRef<HTMLDivElement>(null);

  const handleClick = (index: number, tab: Tab) => {
    onChange?.(index);
    tab.onClick?.();
  };

  const handleMouseEnter = (index: number) => {
    setHovered(index);
  };

  const handleMouseLeave = () => {
    setHovered(null);
  };

  const Separator = () =>
    orientation === "horizontal" ? (
      <div className="mx-1 h-[24px] w-[1.2px] bg-border" aria-hidden="true" />
    ) : (
      <div className="my-1 h-[1.2px] w-[24px] bg-border" aria-hidden="true" />
    );

  const isVertical = orientation === "vertical";
  const buttonVariants = isVertical ? buttonVariantsVertical : buttonVariantsHorizontal;
  const spanVariants = isVertical ? spanVariantsVertical : spanVariantsHorizontal;

  return (
    <div
      ref={outsideClickRef}
      className={cn(
        "flex items-center gap-2 rounded-2xl border bg-background p-1 shadow-sm",
        isVertical ? "flex-col" : "flex-wrap",
        className
      )}
    >
      {tabs.map((tab, index) => {
        if (tab.type === "separator") {
          return <Separator key={`separator-${index}`} />;
        }

        const Icon = tab.icon;
        const isHovered = hovered === index;
        return (
          <motion.button
            key={tab.title}
            variants={buttonVariants}
            initial={false}
            animate="animate"
            custom={isHovered}
            onClick={() => handleClick(index, tab)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            transition={transition}
            title={tab.title}
            className={cn(
              "relative flex items-center rounded-xl text-sm font-medium transition-colors duration-300",
              isVertical ? "flex-col px-2 py-2 w-full" : "px-4 py-2",
              isHovered
                ? cn("bg-muted", tab.color || activeColor)
                : "text-muted-foreground hover:bg-muted"
            )}
          >
            <Icon size={20} />
            <AnimatePresence initial={false}>
              {isHovered && (
                <motion.span
                  variants={spanVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={transition}
                  className={cn(
                    "overflow-hidden text-xs font-medium",
                    isVertical ? "text-center" : ""
                  )}
                >
                  {tab.title}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        );
      })}
    </div>
  );
}
